// src/services/questionnaire.service.ts
import { useApiClientStore } from '../stores/useApiClientStore';

import { ApiResponseRaw, ApiResult, PatientDetails } from '../types';
import { Questionnaire, QuestionnaireAnswers } from '../types/questionnaire';

/**
 * Paths are not settled — they arrive with the V2 questionnaire endpoints. They
 * live here as constants so re-pointing them is a one-line change.
 */
export const questionnairePath = (requestId: string) =>
  `/patient-portal/questionnaire/${requestId}`;

/**
 * Its own handler, not the shared `authenticate-using-patient-details`: it verifies
 * the questionnaire link and drops the can-be-communicated-with gate for V1 parity.
 * The session it issues lasts 12h (measured against `user_session`, not 24h).
 */
export async function authenticateQuestionnaireApi(
  requestId: string,
  details: PatientDetails,
): Promise<ApiResult> {
  const api = useApiClientStore().getClient();

  // `skipErrorHandler` for the same reason as the GET: a mistyped postcode should
  // leave the patient on the challenge to correct it, not on the error page.
  const response = await api.postRaw<{ ok: boolean }>(
    `${questionnairePath(requestId)}/authenticate`,
    details,
    { skipErrorHandler: true },
  );

  return response.ok ? { ok: true } : { ok: false, errors: response.errors };
}

/**
 * `skipErrorHandler` because the link, not the session, is the entry point: an
 * unauthenticated visitor belongs on the demographics challenge, and the global
 * hook would send them to the error page before the store could decide.
 */
export async function fetchQuestionnaireApi(
  requestId: string,
): Promise<ApiResponseRaw<Questionnaire>> {
  const api = useApiClientStore().getClient();

  return api.getRaw<Questionnaire>(questionnairePath(requestId), { skipErrorHandler: true });
}

/** `versionNumber` is required — the response is filed against the version answered, not the current one. */
export async function submitQuestionnaireApi(
  requestId: string,
  versionNumber: string,
  answers: QuestionnaireAnswers,
): Promise<ApiResult> {
  const api = useApiClientStore().getClient();

  return api.postRaw<ApiResult>(questionnairePath(requestId), { versionNumber, answers });
}
